import React, { useState, useCallback } from 'react';
import { formatCurrency } from '../../utils/formatCurrency';
import { getTipOptions } from '../../utils/calculateTotals';

/**
 * TipSelector - Large touch-friendly tip selection
 * 
 * Features:
 * - No Tip + the restaurant's own presets (kiosk config payment.tip_presets)
 * - Custom tip input option
 * - Clear selected state
 * - Live amount calculation
 * - Minimum 56px touch targets
 */
export default function TipSelector({
  subtotal = 0,
  presets = [],
  selectedPercentage = null, // null, 0, a preset, 'custom'
  customAmount = 0,
  onSelect,
  onCustomAmountChange,
  className = '',
}) {
  const [showCustomInput, setShowCustomInput] = useState(selectedPercentage === 'custom');
  const [customInputValue, setCustomInputValue] = useState(customAmount > 0 ? customAmount.toString() : '');

  // No Tip + the owner's presets
  const options = getTipOptions(subtotal, presets).map((o) => ({ value: o.percentage, label: o.label, amount: o.amount }));

  const handlePresetSelect = useCallback((value) => {
    setShowCustomInput(false);
    onSelect(value);
  }, [onSelect]);

  const handleCustomClick = useCallback(() => {
    setShowCustomInput(true);
    onSelect('custom');
  }, [onSelect]);

  const handleCustomInputChange = useCallback((e) => {
    const value = e.target.value;
    // Only allow numbers and one decimal point
    if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
      setCustomInputValue(value);
      const numValue = parseFloat(value) || 0;
      onCustomAmountChange(numValue);
    }
  }, [onCustomAmountChange]);

  const handleCustomInputBlur = useCallback(() => {
    // Format to 2 decimal places on blur
    if (customInputValue) {
      const numValue = parseFloat(customInputValue) || 0;
      setCustomInputValue(numValue.toFixed(2));
    }
  }, [customInputValue]);

  return (
    <div className={className}>
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold text-white mb-2">
          Add a tip?
        </h2>
        <p className="text-gray-400">
          100% of tips go to our team
        </p>
      </div>

      {/* Preset Options Grid */}
      <div className={`grid gap-3 mb-4 ${({ 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' })[options.length] || 'grid-cols-5'}`}>
        {options.map((preset) => {
          const isSelected = selectedPercentage === preset.value && !showCustomInput;
          
          return (
            <button
              key={preset.value}
              onClick={() => handlePresetSelect(preset.value)}
              className={`
                h-20 flex flex-col items-center justify-center
                rounded-2xl border-2
                transition-all duration-150
                ${isSelected
                  ? 'bg-[#f5a623]/10 border-[#f5a623] text-[#f5a623]'
                  : 'bg-[#1a1f26] border-white/10 text-white hover:border-white/20 hover:bg-[#252b33]'
                }
                active:scale-[0.97]
              `}
              aria-pressed={isSelected}
              data-testid={`kiosk-tip-${preset.value}`}
              aria-label={`${preset.label}${preset.amount > 0 ? ` - ${formatCurrency(preset.amount)}` : ''}`}
            >
              <span className="text-lg font-semibold">{preset.label}</span>
              {preset.amount > 0 && (
                <span className={`text-sm mt-1 ${isSelected ? 'text-[#f5a623]/80' : 'text-gray-500'}`}>
                  {formatCurrency(preset.amount)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom Tip Option */}
      <button
        onClick={handleCustomClick}
        className={`
          w-full h-14 px-4
          flex items-center justify-between
          rounded-xl border-2
          transition-all duration-150
          ${showCustomInput
            ? 'bg-[#f5a623]/10 border-[#f5a623]'
            : 'bg-[#1a1f26] border-white/10 hover:border-white/20 hover:bg-[#252b33]'
          }
        `}
        aria-expanded={showCustomInput}
      >
        <span className={`font-medium ${showCustomInput ? 'text-[#f5a623]' : 'text-white'}`}>
          Custom Amount
        </span>
        {!showCustomInput && (
          <span className="text-gray-500">Enter amount →</span>
        )}
      </button>

      {/* Custom Input Field */}
      {showCustomInput && (
        <div className="mt-3 animate-slide-down">
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400">
              $
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={customInputValue}
              onChange={handleCustomInputChange}
              onBlur={handleCustomInputBlur}
              placeholder="0.00"
              autoFocus
              className="
                w-full h-16 pl-10 pr-4
                bg-[#252b33] border-2 border-[#f5a623]
                rounded-xl
                text-2xl text-white text-center font-semibold
                placeholder-gray-600
                focus:outline-none
              "
              aria-label="Custom tip amount in dollars"
            />
          </div>
        </div>
      )}

      {/* Animation */}
      <style>{`
        @keyframes slide-down {
          from { opacity: 0; max-height: 0; margin-top: 0; }
          to { opacity: 1; max-height: 100px; margin-top: 12px; }
        }
        .animate-slide-down {
          animation: slide-down 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
